import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import classNames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
    BEST_OF_CITY_EVENTS,
    BUCKET_TYPE_PROFILE,
    POPULAR_EVENTS,
    PRIMARY_BUCKET,
} from '../../../../constants/constants';
import { ALL_TAB, FOR_YOU, FREE_TAB } from '../../../../constants/tabConfig';
import { useHasEnteredViewport } from '../../../../hooks';
import {
    BaseSeasonalContentShape,
    BucketConfig,
    Events,
    EventSearch,
    FeatureFlags,
    GASettings,
    OrganizerProfiles,
    SearchCriteriaShape,
} from '../../../../types/index';
import { ConsumerCards } from '../../components/ConsumerCards';
import { OrganizerCards } from '../../components/OrganizerCards';
import {
    clickOnOnboardingCard,
    clickOnSeeMore,
    clickOnViewEvent,
    fetchUserSavedEvents,
} from '../../redux/events';
import { getBucketConfig, getEventsLayout } from '../../utils';
import {
    DESKTOP_DEFAULT_LAYOUT_CONFIG,
    LAYOUT_WIDTH_MAP,
    MOBILE_DEFAULT_LAYOUT_CONFIG,
    ONLINE_EVENTS,
} from './BucketContainer.constants';
import { useWindowWidth } from './BucketContainer.hooks';
import './BucketPremium.scss';

const SeeMoreOfBucket = loadable(() => import('./SeeMoreOfBucket'));
const EmptyState = loadable(() => import('../../components/EmptyState'), {
    resolveComponent: (components: { EmptyState: React.ReactElement }) =>
        components.EmptyState,
});

const _isInOddLayout = (windowWidth: number) =>
    windowWidth >= LAYOUT_WIDTH_MAP.odd && windowWidth < LAYOUT_WIDTH_MAP.even;

interface BucketHeaderProps {
    currentPlace?: string;
    isPrimaryBucket?: boolean;
    isSpecialBucket?: boolean;
    name?: $FixMe;
    subtitle?: $FixMe;
    icon?: React.ReactNode;
}

const BucketHeader = (props: BucketHeaderProps) => {
    if (!props.name && !props.subtitle) {
        return null;
    }

    let titleComponent = null;
    let subtitleComponent = null;

    if (props.name) {
        let parsedName: any = props.name;

        const titleClassNames = classNames(
            'eds-text-color--grey-900',
            'eds-text-hs',
        );

        if (props.name.lazyIncludeLocation) {
            parsedName = props.name.lazyIncludeLocation?.(
                props.currentPlace || '',
            );
        }

        if (props.currentPlace === ONLINE_EVENTS && props.isPrimaryBucket) {
            parsedName = gettext('Online Events');
        }

        titleComponent = <h3 className={titleClassNames}>{parsedName}</h3>;
    }

    if (props.subtitle && props.subtitle.lazyIncludeLocation) {
        const parsedSubtitle = props.subtitle.lazyIncludeLocation(
            props.currentPlace || '',
        );

        subtitleComponent = (
            <span className="eds-text-bm eds-text-color--grey-600">
                {parsedSubtitle}
            </span>
        );
    }

    const bucketHeaderClassNames = classNames(
        'feed__bucket-header',
        'eds-l-pad-bot-6',
        {
            'eds-l-sm-pad-bot-10 eds-l-sw-pad-bot-8 pad-bot-mn-up': !props.isSpecialBucket,
            'pad-bot-sm-up--premium': props.isSpecialBucket,
            'has-icon': props.icon,
        },
        'eds-l-mn-pad-hor-3',
        'eds-l-md-pad-hor-3',
        'eds-l-mw-pad-hor-3',
        'eds-l-ln-pad-hor-3',
        'eds-l-lg-pad-hor-3',
        'eds-l-lw-pad-hor-3',
    );

    let bucketHeader = null;

    if (subtitleComponent || titleComponent) {
        bucketHeader = (
            <div
                className={bucketHeaderClassNames}
                data-testid="feed-bucket-header"
            >
                {props.icon || null}
                {titleComponent}
                <div>{subtitleComponent}</div>
            </div>
        );
    }

    return bucketHeader;
};

const Bucket = ({
    events = [],
    profiles = [],
    searchCriteria = {},
    bucketConfig,
    conditionalRender,
    ...props
}: {
    events?: Events;
    profiles?: OrganizerProfiles;
    searchCriteria?: SearchCriteriaShape;
    bucketConfig?: BucketConfig;
    bucketKey?: string;
    conditionalRender: boolean;
    currentPlace?: string;
    isAuthenticated?: boolean;
    locationSlug?: string;
    name?: {
        lazyIncludeLocation?: (arg0: string) => string | GenericLazyString;
    };
    onClickOnBoardingCard?: Function;
    onSeeMore?: Function;
    onViewEvent?: Function;
    seeMore?: { url?: string };
    seasonalContent?: BaseSeasonalContentShape[];
    shouldShowSeeMore?: boolean;
    subtitle?: {
        lazyIncludeLocation?: (arg0: string) => string | GenericLazyString;
    };
    tabKey?: string;
    type?: string;
    userId?: string;
    icon?: React.ReactNode;
    isGDPRCountry: boolean;
    fetchUserSavedEvents: Function;
    gaSettings: GASettings;
    gaCategory: string;
    props: any;
}) => {
    const [animateIn, setAnimateIn] = useState(false);
    const ref = useRef(null);
    const isInViewport = useHasEnteredViewport(ref, {
        visibleInitial: !conditionalRender,
    });

    useEffect(() => {
        if (isInViewport && !animateIn) {
            setAnimateIn(true);
        }
    }, [isInViewport, animateIn]);

    const windowWidth = useWindowWidth();
    const isMobile = (windowWidth || 0) <= 660;

    const bucketClassNames = React.useMemo(() => {
        const bucketClassNamesDesktop = classNames(
            'feed-events-bucket',
            `feed-events--${props.bucketKey}`,
            {
                'feed-events-bucket--animated': isInViewport,
                'feed-events-bucket--animate': animateIn,
            },
        );
        const bucketClassNamesMobile = classNames(
            bucketClassNamesDesktop,
            'feed-events-bucket--mobile',
        );

        return isMobile ? bucketClassNamesMobile : bucketClassNamesDesktop;
    }, [isMobile, animateIn, props.bucketKey, isInViewport]);

    const layoutConfig = React.useMemo(() => {
        const overwrite = isMobile
            ? 'overwrite.props.layout.mobile.events'
            : 'overwrite.props.layout.desktop.events';
        const defaultConfig = isMobile
            ? MOBILE_DEFAULT_LAYOUT_CONFIG
            : DESKTOP_DEFAULT_LAYOUT_CONFIG;

        return getBucketConfig({
            bucketConfig: get(bucketConfig, overwrite, {}),
            defaultConfig,
        });
    }, [isMobile, bucketConfig]);

    const eventGroups = React.useMemo(() => {
        if (isMobile) {
            return getEventsLayout({
                events,
                bucketConfig: layoutConfig,
            });
        }

        return getEventsLayout({
            events,
            bucketConfig: layoutConfig,
        });
    }, [isMobile, layoutConfig, events]);

    if (isEmpty(events) && props.tabKey !== FOR_YOU) {
        return (
            <EmptyState
                bucketKey={props.bucketKey}
                emptyState={bucketConfig?.emptyState}
                isAuthenticated={props.isAuthenticated}
            />
        );
    }

    return (
        <section ref={ref}>
            {isInViewport ? (
                <div className={bucketClassNames}>
                    <BucketHeader
                        name={props.name}
                        subtitle={props.subtitle}
                        currentPlace={props.currentPlace}
                        isPrimaryBucket={props.bucketKey === PRIMARY_BUCKET}
                        isSpecialBucket={[
                            BEST_OF_CITY_EVENTS,
                            POPULAR_EVENTS,
                        ].includes(props.bucketKey || '')}
                        icon={props.icon}
                    />

                    <ConsumerCards
                        bucketKey={props.bucketKey}
                        fetchUserSavedEvents={props.fetchUserSavedEvents}
                        isAuthenticated={props.isAuthenticated}
                        isFreeTab={searchCriteria.price === FREE_TAB}
                        isFirstBucket={props.tabKey !== ALL_TAB}
                        isInOddLayout={_isInOddLayout(windowWidth || 0)}
                        onClickOnBoardingCard={props.onClickOnBoardingCard}
                        onViewEvent={props.onViewEvent}
                        shouldShowOnboardingCard={get(
                            bucketConfig,
                            'shouldShowOnboardingCard',
                            false,
                        )}
                        bucketLayoutConfig={layoutConfig}
                        eventsGroups={eventGroups}
                        cardType="grid"
                    />

                    {props.type === BUCKET_TYPE_PROFILE && (
                        <OrganizerCards
                            bucketKey={props.bucketKey}
                            profiles={profiles}
                            isAuthenticated={props.isAuthenticated || false}
                            userId={props.userId}
                            isGDPRCountry={props.isGDPRCountry || false}
                            gaSettings={props.gaSettings}
                        />
                    )}

                    {props.shouldShowSeeMore && (
                        <SeeMoreOfBucket
                            bucketKey={props.bucketKey}
                            seeMore={props.seeMore}
                            onSeeMore={props.onSeeMore}
                            searchCriteria={searchCriteria}
                            shouldShowSeeMore={true}
                            locationSlug={props.locationSlug}
                            tabKey={props.tabKey}
                            userId={props.userId}
                            seasonalContent={props.seasonalContent}
                        />
                    )}
                </div>
            ) : (
                <div className={bucketClassNames} />
            )}
        </section>
    );
};

const _mapStateToProps = (state: {
    app: {
        isMobile?: boolean;
        isGDPRCountry?: boolean;
        featureFlags: FeatureFlags;
    };
    user: { isAuthenticated?: boolean; publicId?: string };
    content?: {
        browseState?: {
            tabKey?: string;
        };
        flatBucket?: {
            eventSearch?: EventSearch;
            results?: Events;
        };
        seasonal?: any[];
    };
    location?: {
        currentPlace?: string;
        slug?: string;
    };
    gaSettings: GASettings;
}) => ({
    isMobile: state.app.isMobile,
    isAuthenticated: state.user.isAuthenticated,
    searchCriteria: state.content?.flatBucket
        ? state.content.flatBucket.eventSearch
        : { dates: ['current_future'] },
    locationSlug: state.location?.slug,
    currentPlace: state.location?.currentPlace,
    tabKey: state.content?.browseState?.tabKey,
    userId: state.user.publicId,
    seasonalContent: state.content?.seasonal,
    gaSettings: state.gaSettings,
    isGDPRCountry: state.app.isGDPRCountry,
});

const _mapDispatchToProps = {
    onSeeMore: clickOnSeeMore,
    onViewEvent: clickOnViewEvent,
    onClickOnBoardingCard: clickOnOnboardingCard,
    fetchUserSavedEvents,
};

export default connect(_mapStateToProps, _mapDispatchToProps)(Bucket);
